// ShipperCarrierManagementPage
// This page renders the "Shipper -> Carriers" relation as a standalone route,
// preserving per-tab isolation and persistence as before.
//
// Routes that should land here (already configured in your router):
//   /shipper-carriers   -> renders ShipperCarrierManagementPage
//
// Notes:
// - We keep per-tab state in M_State, under the selected tab id namespace.
// - Children logic already persists its own state per tab; this stays intact.
// - We update the tab title using the i18n key that matches the MenuDisplay
//   value from the backend seed:
//      MenuShipperCarriersManagementLabel
// - We do not auto-navigate between routes here; we only render this section.
//
// --------------------------------------------------------------------------------------
// Notes 
// - Carriers list is NOT preloaded; it's paginated, searched and ordered by SelectableTileGrid.
// - Pre-linked carriers (for the selected shipper) appear CHECKED via selectedIds.
// - Toggling a carrier uses the existing endpoints (POST/DELETE) and updates state accordingly.
// - Per-tab isolation & persistence: persist (a) selected shipper id and (b) the grid state
//    under the current tab namespace, rehydrating on tab switch.
// - Shared look and feel via relation-management.css
// --------------------------------------------------------------------------------------

import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LoadingContext,
  MButton,
  M_StateActions,
  NavigateContext,
} from 'src/M_Components';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import '../relation-management.css';  // Shared CSS for relation management pages

// Generic, paginated selector
import SelectableTileGrid from '../../components/SelectableTileGrid/SelectableTileGrid';

// Dynamic select box (server-driven, no preloaded options)
import DynamicSelectBox from 'src/components/Forms/DynamicSelectBox';

export default function ShipperCarrierManagementPage({ toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const matches = useMediaQuery('(min-width:900px)');

  // ------------------------------------------------------------------------------------------------------------
  // Read current selected tab id so we can:
  //   1) Safely rename the tab caption.
  //   2) Bind this component instance to a tab id for proper per-tab isolation.
  // ------------------------------------------------------------------------------------------------------------
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  // Keep a local namespace id that tracks the current tab id when available.
  // This allows us to rehydrate correctly even if selectedTab changes over time.
  const [id, setId] = useState(() => selectedTab);

  // Flag to ensure we always give priority to session restore before defaultShipper logic.
  const [hasRestoredFromSession, setHasRestoredFromSession] = useState(false);

  // ------------------------------------------------------------------------------------------------------------
  // EFFECT
  // Update tab title when language changes. This keeps the tab label contextual.
  // IMPORTANT: we use the same i18n key as the backend MenuDisplay value:
  //   MenuShipperCarriersManagementLabel
  // ------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTab) return;
    renameTab(String(selectedTab), t('MenuShipperCarriersManagementLabel'));
  }, [selectedTab, renameTab, t]);

  // --- Local state (small & focused) ---------------------------------------------------
  // Removed local shippers array: now Shipper select is fully server-driven by DynamicSelectBox
  const [linkedCarriers, setLinkedCarriers] = useState([]);

  // Store only the selected shipper id
  const [selectedShipperId, setSelectedShipperId] = useState(null);

  // Keys inside the tab namespace (keep short & explicit)
  const K = {
    selectedShipperId: 'SCM.selectedShipperId',
    gridCarriers: 'SCM.grid.carriers',
  };

  // Helpers to read/write a single key under the current tab namespace
  const upsertSession = (tabId, kv) => {
    const current = M_State.find((o) => o.key === tabId)?.value || [];
    const filtered = current.filter((e) => e.key !== kv.key);
    dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
  };
  const pick = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

  // Stable base url + buffers
  const baseUrl = ctx.DEFAULTURL;
  const shipperBuffer = useMemo(
    () => new BufferService('Shippers', baseUrl),
    [baseUrl]
  );

  // ============================================================================================================ 
  // Rehydrate per-tab state (tab switch or first mount)
  // - We resolve the current tab id from selectedTab or local id.
  // - This keeps per-tab isolation while still allowing re-opened tabs to recover their state.
  // - This effect is also responsible for deciding if there is a saved shipper id before
  //   we fall back to defaultShipper/localStorage logic.
  // ============================================================================================================
  useEffect(() => {
    const currentTabId = selectedTab || id;
    if (!currentTabId) return;

    if (currentTabId !== id) {
      setId(currentTabId);
    }

    const session = M_State.find((obj) => obj.key === currentTabId)?.value;

    if (session) {
      const savedShipperId = pick(session, K.selectedShipperId);
      if (savedShipperId !== undefined && savedShipperId !== null) {
        setSelectedShipperId(savedShipperId);
        setHasRestoredFromSession(true);
        return;
      }
    }

    // Even if there is no session or no saved shipper id, we mark that restoration was attempted.
    setHasRestoredFromSession(true);
  }, [M_State, selectedTab, id]);

  // Persist selected shipper id per-tab
  useEffect(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return;
    upsertSession(currentTabId, { key: K.selectedShipperId, value: selectedShipperId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedTab, selectedShipperId]);

  // ===================================================================================================================
  // Initial selection strategy for Shipper
  // ===================================================================================================================
  // Don't preload options. Use DynamicSelectBox to fetch. For default behavior:
  // 1) If per-tab state already set, keep it (restored above).
  // 2) Else if localStorage defaultShipperId is valid (>0), use it.
  // 3) Else fetch the first page (PageSize=1, active only, ordered by Name asc) and use its first Id, if any.
  useEffect(() => {
    // Wait until we have tried to restore from session, so saved state always wins.
    if (!hasRestoredFromSession) return;

    // If already selected (from session), do nothing
    if (selectedShipperId != null) return;

    const normalizeId = (v) => {
      if (v === 0 || v === '0' || v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    // Try localStorage defaultShipperId
    const stored = normalizeId(localStorage.getItem('defaultShipperId'));
    if (stored != null) {
      setSelectedShipperId(stored);
      return;
    }

    // Fallback: minimal fetch to get the first Shipper without preloading options to the select
    (async () => {
      try {
        const order = encodeURIComponent('Name asc');
        const filter = encodeURIComponent('Inactive eq false');
        const res = await shipperBuffer.filter(
          `?Page=1&PageSize=1&OrderBy=${order}&Filter=${filter}`
        );
        if (isFailurePayload(res)) return;
        const items = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : [];
        const first = items?.[0];
        if (first?.Id != null) setSelectedShipperId(first.Id);
      } catch {
        // silent fallback: leave unselected
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRestoredFromSession, shipperBuffer, selectedShipperId]);

  // ==========================================================================================================
  //  Load existing relations when a Shipper is selected 
  // ==========================================================================================================
  useEffect(() => {
    if (!selectedShipperId) {
      setLinkedCarriers([]);
      return;
    }
    ctx.setIsLoad(true);
    const shipperCarriersBuffer = new BufferService(
      `Shippers/${selectedShipperId}/Carriers`,
      baseUrl
    );
    shipperCarriersBuffer
      .get()
      .then((data) => {
        if (isFailurePayload(data)) {
          toastMessages('error', '', apiMessage(data, t('ErrorLoadingData')));
          return;
        }
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.Items)
            ? data.Items
            : [];
        setLinkedCarriers(arr.map((c) => c.Id).filter((v) => v != null));
      })
      .catch((err) =>
        toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')))
      )
      .finally(() => ctx.setIsLoad(false));
  }, [selectedShipperId, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================================================ 
  // Read persisted grid state (per tab) for initialState 
  // ============================================================================================================
  const gridInitialState = useMemo(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return undefined;
    const session = M_State.find((o) => o.key === currentTabId)?.value || [];
    return pick(session, K.gridCarriers) || undefined;
  }, [M_State, id, selectedTab]);

  // ============================================================================================================ 
  // Render 
  // ============================================================================================================
  return (
    <div className="divGlobalSettings" style={{ padding: '20px' }}>
      {/* Title area shows the section title based on i18n keys */}
      <div
        className="titleSettings"
        style={{
          color: ctx.secondaryColor,
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        {t('MenuShipperCarriersManagementLabel')}
      </div>

      {/* Keep layout responsive as before. Only the Shipper/Carriers relation content is rendered here. */}
      <div style={matches ? { display: 'flex' } : {}}>
        <div className="settingsPageMainDiv" style={{ width: '100%' }}>
          <div className="firstDivContainer rel-card">
            {/* Shipper select (server-driven via DynamicSelectBox, active only, no preloaded options) */}
            <div className="rel-section">
              <span className="rel-title">{t('Shipper')}</span>
              <DynamicSelectBox
                // Use DynamicSelectBox to avoid loading large option lists upfront
                label={t('Shipper')}
                tableName="Shippers"
                groupBy="Name"
                valueId={selectedShipperId}
                required
                // Keep pageSize modest; options are fetched lazily
                pageSize={20}
                // When the user selects a shipper, store only the id
                onChange={(opt) => setSelectedShipperId(opt?.value ?? null)}
                // No initialQuery is required; the component auto loads page 1 on open
              />
            </div>

            {/* Carriers grid (only after selecting a shipper) */}
            {selectedShipperId && (
              <div className="rel-grid-section">
                <div className="rel-grid-box-500-700">
                  <SelectableTileGrid
                    /* Business behavior:
                       - Paginated carriers (server-side): endpoint="Carriers"
                       - Only show active carriers (staticFilter)
                       - Already-linked ones appear CHECKED (selectedIds=linkedCarriers)
                       - Toggling add/removes the relation with the selected shipper */
                    title={t('Carriers')}
                    endpoint="Carriers"
                    idField="Id"
                    displayField="Name"
                    pageSize={20}
                    queryable
                    orderable
                    staticFilter="Inactive eq false"
                    selectedIds={linkedCarriers}
                    onItemToggle={(item, next) => {
                      const idVal = item?.Id ?? item?.id;
                      if (idVal != null) toggleCarrier(idVal);
                    }}
                    /* Per-tab persistence of grid state (search/order/pages) */
                    stateScopeId={`${id || selectedTab || 'unknown'}::SCM::carriers`}
                    initialState={gridInitialState}
                    onStateChange={(st) => {
                      const currentTabId = id || selectedTab;
                      if (!currentTabId) return;
                      upsertSession(currentTabId, { key: K.gridCarriers, value: st });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================================================
  // Toggle relation (checked => remove; unchecked => add) 
  // ============================================================================================================
  function toggleCarrier(carrierId) {
    if (!selectedShipperId) return;
    ctx.setIsLoad(true);

    const isLinked = linkedCarriers.includes(carrierId);
    const addBuffer = new BufferService(
      `Shippers/${selectedShipperId}/Carriers`,
      baseUrl
    );
    const removeBuffer = new BufferService(
      `Shippers/${selectedShipperId}/Carriers`,
      baseUrl
    );

    const request = isLinked
      ? removeBuffer.delete(carrierId)
      : addBuffer.post({ carrierId });

    request
      .then((res) => {
        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }
        setLinkedCarriers((prev) =>
          isLinked
            ? prev.filter((id) => id !== carrierId)
            : [...prev, carrierId]
        );
        toastMessages(
          'success',
          '',
          isLinked ? t('CarrierRemoved') : t('CarrierAdded')
        );
      })
      .catch((err) =>
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')))
      )
      .finally(() => ctx.setIsLoad(false));
  }
}

ShipperCarrierManagementPage.propTypes = {
  toastMessages: PropTypes.func.isRequired,
};
